import React, { useState } from "react";

type DefaultImage = "main" | "secondary";

export enum GridLayout {
  SMALL = "small",
  LIST = "list",
  MEDIUM = "medium",
  LARGE = "large",
}

const LOCALSTORAGE_KEY_FOR_GRID_LAYOUT = "gridLayout";
const LOCALSTORAGE_KEY_FOR_SIDEBAR = "sidebarOpen";

type ViewSettingsProps = {
  changeDefaultImage: (value: DefaultImage) => void;
  isMainImage: boolean;
  isSecondaryImage: boolean;
  gridLayout: GridLayout;
  setGridLayout: (layout: GridLayout) => void;
  isLayoutForced: boolean;
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
};

type ViewSettingsProviderProps = ViewSettingsProps | null;

export const ViewSettingsContext = React.createContext<ViewSettingsProviderProps>(null);

const VALID_GRID_LAYOUTS = Object.values(GridLayout) as string[];

function getForcedLayout(): GridLayout | null {
  const mountEl = document.getElementById("reactify-mount-collection");
  const attr = mountEl?.getAttribute("data-grid-layout");
  if (attr && VALID_GRID_LAYOUTS.includes(attr)) {
    return attr as GridLayout;
  }
  return null;
}

export const ViewSettingsProvider: React.FC = ({ children }) => {
  const [defaultImage, setDefaultImage] = useState("main");
  const isMainImage = defaultImage === "main";
  const isSecondaryImage = defaultImage === "secondary";
  const changeDefaultImage = (value: DefaultImage) => {
    setDefaultImage(value);
  };

  const [forcedLayout] = useState<GridLayout | null>(() => getForcedLayout());
  const isLayoutForced = forcedLayout !== null;

  const [gridLayout, setGridLayoutState] = useState<GridLayout>(() => {
    if (forcedLayout) return forcedLayout;
    try {
      const storedLayout = localStorage.getItem(LOCALSTORAGE_KEY_FOR_GRID_LAYOUT);
      return storedLayout ? JSON.parse(storedLayout) : GridLayout.MEDIUM;
    } catch (error) {
      console.error("Error parsing localStorage data:", error);
      return GridLayout.MEDIUM;
    }
  });

  const setGridLayout = (layout: GridLayout) => {
    setGridLayoutState(layout);
    localStorage.setItem(LOCALSTORAGE_KEY_FOR_GRID_LAYOUT, JSON.stringify(layout));
  };

  const [isSidebarOpen, setSidebarOpenState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(LOCALSTORAGE_KEY_FOR_SIDEBAR);
      return stored !== null ? JSON.parse(stored) : true;
    } catch {
      return true;
    }
  });

  const setSidebarOpen = (open: boolean) => {
    setSidebarOpenState(open);
    localStorage.setItem(LOCALSTORAGE_KEY_FOR_SIDEBAR, JSON.stringify(open));
  };

  return (
    <ViewSettingsContext.Provider
      value={{
        changeDefaultImage,
        isMainImage,
        isSecondaryImage,
        gridLayout,
        setGridLayout,
        isLayoutForced,
        isSidebarOpen,
        setSidebarOpen,
      }}
    >
      {children}
    </ViewSettingsContext.Provider>
  );
};
